/**
 * How a validation problem is worded for a person (owner, 2026-10-04: "Jenis: Invalid input" in
 * Periksa). Every sentence comes from ui.id.json: a schema's own sentence stands, and an issue a
 * schema left unworded (Zod's own codes, in English) is worded here by its code, passed as the
 * parse's error map (`safeParse(nilai, { error: petaGalatZod })`; Zod uses it only where the
 * schema gave no sentence). A form's problems are then read one per field, the required one
 * alone when a field is both empty and refused. The ceremony flow's Periksa, its first step and
 * the import read them through here. Pure.
 */
import type { z } from 'zod'
import ui from '../strings/ui.id.json' with { type: 'json' }

const pesan = ui.validasi

const kosong = (input: unknown): boolean =>
  input === undefined || input === null || (typeof input === 'string' && input.trim() === '')

/**
 * Zod's own issue in the app's words: nothing there reads "Wajib diisi.", a value outside a
 * choice "Pilih dari daftar.", a text or a number past its bound says so ("Isinya terlalu
 * panjang.", "Angkanya terlalu besar.": final review 3, Minor 15), anything else "Isinya tidak
 * dikenal."; never Zod's English.
 */
export const petaGalatZod: z.core.$ZodErrorMap = (issue) => {
  switch (issue.code) {
    case 'invalid_type':
      return kosong(issue.input) ? pesan.wajib : pesan.tidakDikenal
    case 'too_small':
      if (kosong(issue.input)) return pesan.wajib
      return issue.origin === 'number' || issue.origin === 'bigint'
        ? pesan.angkaTerlaluKecil
        : issue.origin === 'string'
          ? pesan.terlaluPendek
          : pesan.tidakDikenal
    case 'too_big':
      return issue.origin === 'number' || issue.origin === 'bigint'
        ? pesan.angkaTerlaluBesar
        : issue.origin === 'string'
          ? pesan.terlaluPanjang
          : pesan.tidakDikenal
    case 'invalid_value':
      return pesan.pilihDariDaftar
    default:
      return pesan.tidakDikenal
  }
}

/** An issue as the wording reads it: its field (`path[0]`) and its sentence. */
export interface IssueRingkas {
  readonly path: readonly PropertyKey[]
  readonly message: string
}

export interface MasalahKolom {
  /** The field (`path[0]`); '' for the form as a whole. */
  kolom: string
  pesan: string
}

/**
 * A form's problems, one sentence per field in the order the fields are met: "Wajib diisi."
 * alone when it is among a field's (an empty choice never also reads as a refused one), else
 * the field's first.
 */
export function masalahPerKolom(issues: readonly IssueRingkas[]): MasalahKolom[] {
  const perKolom = new Map<string, string>()
  for (const issue of issues) {
    const kolom = String(issue.path[0] ?? '')
    const ada = perKolom.get(kolom)
    if (ada === undefined || (issue.message === pesan.wajib && ada !== pesan.wajib)) {
      perKolom.set(kolom, issue.message)
    }
  }
  return [...perKolom].map(([kolom, p]) => ({ kolom, pesan: p }))
}
